import type { Query, QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { hashKey } from '@tanstack/react-query';
import type { z } from 'zod';

import {
  incidentDetailSchema,
  incidentListQuerySchema,
  incidentPageSchema,
} from '@solidops/shared/incidents';
import type {
  IncidentDetailDto,
  IncidentListViewDto,
  IncidentPageDto,
  IncidentPriorityCommandDto,
  IncidentTeamCommandDto,
} from '@solidops/shared/incidents';

import {
  requestIncidentAcknowledgement,
  requestIncidentResolution,
  requestIncidentPriorityChange,
  requestIncidentTeamChange,
} from './incidents.commands.js';
import type { IncidentCommandOptions, IncidentVersionedCommandAuth } from './incidents.commands.js';
import { ApiError, queryClient } from './client.js';
import {
  getGetIncidentQueryKey,
  getGetIncidentQueryOptions,
  getListIncidentsQueryKey,
  getListIncidentsQueryOptions,
  getIncident as getIncidentRequest,
  listIncidents as listIncidentsRequest,
} from './generated/incidents/incidents.js';
import type { ListIncidentsParams } from './generated/model/index.js';

export type IncidentListParams = ListIncidentsParams;
type IncidentQueryOptions<T> = UseQueryOptions<T, ApiError> & { queryKey: QueryKey };
type ReadOptions = { signal?: AbortSignal };

export const getIncidentQueryKey = getGetIncidentQueryKey;

export function getIncidentsQueryKey(input: IncidentListParams = {}): QueryKey {
  return getListIncidentsQueryKey(incidentListQuerySchema.parse(input));
}

export function changeIncidentView(
  input: IncidentListParams,
  view: IncidentListViewDto,
): IncidentListParams {
  const { cursor, ...params } = incidentListQuerySchema.parse(input);
  return { ...params, view, ...(params.view === view && cursor ? { cursor } : {}) };
}

function isIncidentQuery(query: { queryKey: QueryKey }): boolean {
  const path = query.queryKey[0];
  const root = getListIncidentsQueryKey()[0];
  return typeof path === 'string' && (path === root || path.startsWith(`${root}/`));
}

function evictIncidentScope(failedKey: QueryKey, previous: Query[]): void {
  const failedHash = hashKey(failedKey);
  const current = queryClient.getQueryCache().findAll({ predicate: isIncidentQuery });
  for (const query of new Set([...previous, ...current])) {
    // Notify observers even when the transport has already detached their query on 401.
    query.setState({ data: undefined, dataUpdatedAt: 0 });
    // Keep the failing request alive to deliver its ApiError.
    if (query.queryHash !== failedHash || query.state.fetchStatus !== 'fetching') {
      queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
    }
  }
}

export async function readIncident<T>(
  request: () => Promise<unknown>,
  schema: z.ZodType<T>,
  queryKey: QueryKey,
): Promise<T> {
  const previous = queryClient.getQueryCache().findAll({ predicate: isIncidentQuery });
  try {
    return schema.parse(await request());
  } catch (error) {
    if (error instanceof ApiError && [401, 403, 404].includes(error.status)) {
      evictIncidentScope(queryKey, previous);
    }
    throw error;
  }
}

export async function listIncidents(
  input: IncidentListParams = {},
  options?: ReadOptions,
): Promise<IncidentPageDto> {
  const params = incidentListQuerySchema.parse(input);
  return await readIncident(
    async () => await listIncidentsRequest(params, options),
    incidentPageSchema,
    getListIncidentsQueryKey(params),
  );
}

export async function getIncident(
  incidentId: string,
  options?: ReadOptions,
): Promise<IncidentDetailDto> {
  return await readIncident(
    async () => await getIncidentRequest(incidentId, options),
    incidentDetailSchema,
    getIncidentQueryKey(incidentId),
  );
}

export function getIncidentsQueryOptions(
  input: IncidentListParams = {},
): IncidentQueryOptions<IncidentPageDto> {
  const params = incidentListQuerySchema.parse(input);
  return {
    ...getListIncidentsQueryOptions<IncidentPageDto, ApiError>(params),
    queryFn: async ({ signal }) => await listIncidents(params, { signal }),
  };
}

export function getIncidentQueryOptions(
  incidentId: string,
): IncidentQueryOptions<IncidentDetailDto> {
  return {
    ...getGetIncidentQueryOptions<IncidentDetailDto, ApiError>(incidentId),
    queryFn: async ({ signal }) => await getIncident(incidentId, { signal }),
  };
}

export async function invalidateIncidentQueries(): Promise<void> {
  await queryClient.invalidateQueries({ predicate: isIncidentQuery });
}

export async function acknowledgeIncident(
  incidentId: string,
  csrfToken: string,
  options?: IncidentCommandOptions,
): Promise<IncidentDetailDto> {
  const detail = await readIncident(
    async () => await requestIncidentAcknowledgement(incidentId, csrfToken, options),
    incidentDetailSchema,
    getIncidentQueryKey(incidentId),
  );
  await invalidateIncidentQueries();
  return detail;
}

export async function resolveIncident(
  incidentId: string,
  csrfToken: string,
  options?: IncidentCommandOptions,
): Promise<IncidentDetailDto> {
  const detail = await readIncident(
    async () => await requestIncidentResolution(incidentId, csrfToken, options),
    incidentDetailSchema,
    getIncidentQueryKey(incidentId),
  );
  await invalidateIncidentQueries();
  return detail;
}

export async function changeIncidentPriority(
  incidentId: string,
  input: IncidentPriorityCommandDto,
  auth: IncidentVersionedCommandAuth,
  options?: IncidentCommandOptions,
): Promise<IncidentDetailDto> {
  const detail = await readIncident(
    async () => await requestIncidentPriorityChange(incidentId, input, auth, options),
    incidentDetailSchema,
    getIncidentQueryKey(incidentId),
  );
  await invalidateIncidentQueries();
  return detail;
}

export async function changeIncidentTeam(
  incidentId: string,
  input: IncidentTeamCommandDto,
  auth: IncidentVersionedCommandAuth,
  options?: IncidentCommandOptions,
): Promise<IncidentDetailDto> {
  const detail = await readIncident(
    async () => await requestIncidentTeamChange(incidentId, input, auth, options),
    incidentDetailSchema,
    getIncidentQueryKey(incidentId),
  );
  await invalidateIncidentQueries();
  return detail;
}
